/**
 * @file LoginPage.js
 * @description Login page component for handling user authentication with username and password.
 *              Includes functionality for validation, form handling, and navigation based on user role.
 * @author Govind Dindewad
 */

import React, { useState } from 'react';
import './styleLoginPage.css';
import { forgotPassword, getLogin } from '../../services/authService';
import { useDispatch } from 'react-redux';
import { setUserData } from '../../features/users/userSlice';
import { useNavigate } from 'react-router';
import { toast } from 'react-toastify';
import { setRouteStatus } from '../../redux/commonSlice';

function INCSUBLoginPage() {
    // State for login information including username and password
    const [loginInfo, setLoginInfo] = useState({
        username: "",
        password: ""
    });

    const dispatch = useDispatch(); // Redux dispatch for triggering actions
    const navigate = useNavigate(); // Hook for navigation
    const [btnLoader, setBtnLoader] = useState(false); // Loader state for button
    const [errFeild, setErrFeild] = useState(''); // State for managing error field focus
    const [passVisible, setPassVisible] = useState(false); // State to toggle password visibility

    /**
     * Updates the username field in the loginInfo state.
     * @param {Object} e - The event object for the username input field.
     * @returns {void}
     * @author Govind Dindewad
     */
    const handleUsername = (e) => {
        setLoginInfo({
            ...loginInfo,
            username: e.target.value
        });
    };

    /**
     * Updates the password field in the loginInfo state.
     * @param {Object} e - The event object for the password input field.
     * @returns {void}
     * @author Govind Dindewad
     */
    const handlePassword = (e) => {
        setLoginInfo({
            ...loginInfo,
            password: e.target.value
        });
    };

    /**
     * Validates if the given input is a valid mobile number or email.
     * @param {string} input - The input string to validate.
     * @returns {boolean} True if valid, otherwise false.
     * @author Govind Dindewad
     */
    const isValidMobileOrEmail = (input) => {
        const mobileRegex = /^[6-9]\d{9}$/; // Validates Indian mobile numbers
        const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/; // Validates email addresses
        return mobileRegex.test(input) || emailRegex.test(input);
    };

    /**
     * Handles the login process when the login form is submitted.
     * Validates input and communicates with the server for authentication.
     * @param {Object} e - The event object for form submission.
     * @returns {void}
     * @async
     * @author Govind Dindewad
     */
    const handleLogin = async (e) => {
        e.preventDefault();
        setBtnLoader(true);
        if (!isValidMobileOrEmail(loginInfo.username)) {
            setErrFeild('username'); // Highlight username field if invalid
            setBtnLoader(false);
            return;
        }
        if (loginInfo.username !== '' && loginInfo.password !== '') {
            try {
                const response = await getLogin(loginInfo.username, loginInfo.password);
                if (response.status === 200) {
                    // Save user data and token to localStorage and Redux
                    window.localStorage.setItem('userData', JSON.stringify(response.data.data.user));
                    window.localStorage.setItem('token', response.data.data.token);
                    dispatch(setUserData(response.data.user));
                    dispatch(setRouteStatus('/rolemanagement'));
                    if (response.data.data.user.userRole !== 'User') {
                        navigate(`/${response.data.data.user.userRole}`);
                    } else {
                        toast.warning("User Cannot access admin dashboard");
                        window.localStorage.clear();
                    }
                } else if (response.status === 301) {
                    toast.error("Network Error");
                } else if (response.status === 500) {
                    toast.error("Internal server error");
                } else if (![200].includes(response.status)) {
                    toast.error(response.response);
                    if (response.response.data.message === 'Provide valid User Name') {
                        setErrFeild('username');
                    } else if (response.response.data.message === "Password Not Matched") {
                        setErrFeild('password');
                    } else if (response.response.data.message === "User Not Found") {
                        toast.error("User Not Found");
                    }
                }
                setBtnLoader(false);
            } catch (error) {
                toast.error("Something Went Wrong");
                setBtnLoader(false);
            }
        } else {
            setBtnLoader(false);
            setErrFeild('username'); // Highlight username field if empty
        }
    };

    /**
     * Initiates the forgot password process for the user.
     * Validates the input and navigates to the forgot password page.
     * @returns {void}
     * @async
     * @author Govind Dindewad
     */
    const fetchForgotMobile = async () => {
        setBtnLoader(true);
        if (!isValidMobileOrEmail(loginInfo.username)) {
            setErrFeild('username'); // Highlight username field if invalid
            setBtnLoader(false);
            return;
        }
        if (loginInfo.username !== '') {
            try {
                const response = await forgotPassword(loginInfo.username);
                if (response.status === 200) {
                    navigate('/account/forgot_password', {
                        state: response.data.data.userMobileNumber
                    });
                } else if (response.status === 301) {
                    toast.error("Network Error");
                } else if (response.status === 500) {
                    toast.error("Internal server error");
                } else if (![200].includes(response.status)) {
                    toast.error(response.response);
                }
                setBtnLoader(false);
            } catch (error) {
                toast.error("Something Went Wrong");
                setBtnLoader(false);
            }
        } else {
            setBtnLoader(false);
            setErrFeild('username'); // Highlight username field if empty
        }
    };
    return (
        <div className='INCSUBLoginpage'>
            <div className='INCSUBLoginpage_container'>
                <div className='INCSUBLogininfo_container'>
                    <div className='INCSUBLoginpage_headerinfo_container'>
                        <div className='INCSUBLoginpage_header'>
                            <img src='/logo.svg' alt='logo' />
                        </div>
                        <div className='INCSUBLoginpage_subheader'>Please enter your user name and password to continue</div>
                    </div>
                    <div className='INCSUBLoginForm_container'>
                        <form className='INCSUBLogin_form' onSubmit={btnLoader ? null : handleLogin}>
                            <div className='INCSUBUsercred_container login'>
                                <label>User Name</label>
                                <input type='text' name='username' placeholder='Email/Mobile no.' value={loginInfo.username} onChange={handleUsername} style={{ border: errFeild === 'username' ? "1px solid red" : 'none' }} />
                                <p className='errormsg' style={{ display: errFeild === 'username' ? 'block' : 'none' }}>Enter Valid Username</p>
                            </div>
                            <div className='INCSUBUsercred_container login'>
                                <label>Password</label>
                                <div className='INCSUBPassword_feild_container'> <input type={!passVisible ? 'password' : 'text'} name='password' placeholder='Enter Password' value={loginInfo.password} onChange={handlePassword} style={{ border: errFeild === 'password' ? "1px solid red" : 'none' }} />
                                    {!passVisible ? <svg onClick={() => setPassVisible(true)} width="18" height="18" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg">
                                        <mask id="mask0_2333_47666" maskUnits="userSpaceOnUse" x="0" y="2" width="18" height="14">
                                            <path d="M9 6.54901C8.34901 6.54901 7.72469 6.80761 7.26437 7.26793C6.80406 7.72824 6.54545 8.35257 6.54545 9.00355C6.54545 9.65454 6.80406 10.2789 7.26437 10.7392C7.72469 11.1995 8.34901 11.4581 9 11.4581C9.65099 11.4581 10.2753 11.1995 10.7356 10.7392C11.1959 10.2789 11.4545 9.65454 11.4545 9.00355C11.4545 8.35257 11.1959 7.72824 10.7356 7.26793C10.2753 6.80761 9.65099 6.54901 9 6.54901ZM9 13.0945C7.91502 13.0945 6.87448 12.6635 6.10729 11.8963C5.3401 11.1291 4.90909 10.0885 4.90909 9.00355C4.90909 7.91857 5.3401 6.87804 6.10729 6.11084C6.87448 5.34365 7.91502 4.91264 9 4.91264C10.085 4.91264 11.1255 5.34365 11.8927 6.11084C12.6599 6.87804 13.0909 7.91857 13.0909 9.00355C13.0909 10.0885 12.6599 11.1291 11.8927 11.8963C11.1255 12.6635 10.085 13.0945 9 13.0945ZM9 2.86719C5.02364 2.86719 1.45636 5.32173 0 9.00355C1.95545 13.9781 7.56818 16.4163 12.5427 14.4608C15.0382 13.479 17.0182 11.5072 18 9.00355C16.5436 5.32173 12.9764 2.86719 9 2.86719Z" fill="#006FFD" />
                                        </mask>
                                        <g mask="url(#mask0_2333_47666)">
                                            <rect width="18" height="18" fill="#000000" />
                                        </g>
                                    </svg>

                                        :
                                        <svg onClick={() => setPassVisible(false)} width="18" height="18" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg">
                                            <mask id="mask0_2333_47650" maskUnits="userSpaceOnUse" x="0" y="1" width="18" height="16">
                                                <path fill-rule="evenodd" clip-rule="evenodd" d="M17.0394 2.91291C17.4055 2.54678 17.4055 1.95319 17.0394 1.58708C16.6733 1.22097 16.0797 1.22097 15.7135 1.58709L2.21385 15.0871C1.84774 15.4532 1.84774 16.0468 2.21386 16.4129C2.57999 16.779 3.17358 16.779 3.53969 16.4129L5.48496 14.4676C7.67488 15.3214 10.1807 15.3834 12.5427 14.455C15.0382 13.4731 17.0182 11.5013 18 8.99769C17.3593 7.37786 16.31 5.99559 14.997 4.95537L17.0394 2.91291ZM12.7 7.25244L11.4117 8.54077C11.44 8.69035 11.4545 8.84327 11.4545 8.99769C11.4545 9.64868 11.1959 10.273 10.7356 10.7333C10.2753 11.1936 9.65099 11.4522 9 11.4522C8.8456 11.4522 8.6927 11.4377 8.54313 11.4094L7.25483 12.6977C7.79552 12.9527 8.39076 13.0886 9 13.0886C10.085 13.0886 11.1255 12.6576 11.8927 11.8904C12.6599 11.1232 13.0909 10.0827 13.0909 8.99769C13.0909 8.38842 12.955 7.79315 12.7 7.25244ZM9 2.86133C10.0428 2.86133 11.0575 3.03015 12.0134 3.34566L10.255 5.10405C9.85319 4.97451 9.43008 4.90678 9 4.90678C7.91502 4.90678 6.87448 5.33779 6.10729 6.10498C5.3401 6.87218 4.90909 7.91271 4.90909 8.99769C4.90909 9.42781 4.97683 9.85094 5.10639 10.2528L2.62502 12.7342C1.49898 11.7494 0.585532 10.4872 0 8.99769C1.45636 5.31587 5.02364 2.86133 9 2.86133ZM7.26437 7.26207C7.67934 6.8471 8.2276 6.59606 8.80851 6.55062L6.55293 8.80626C6.59835 8.22532 6.84939 7.67705 7.26437 7.26207Z" fill="#006FFD" />
                                            </mask>
                                            <g mask="url(#mask0_2333_47650)">
                                                <rect width="18" height="18" fill="#232323" />
                                            </g>
                                        </svg>

                                    }

                                </div>
                                <p className='errormsg' style={{ display: errFeild === 'password' ? 'block' : 'none' }}>Enter Valid Password</p>
                            </div>
                            <div className='INCSUBForgetpassword'><div onClick={() => fetchForgotMobile()}>Forgot Password?</div></div>
                            <button type='submit' placeholder='Sign In' value={"Sign In"} className='INCSUBLoginForm_submitbutton'>{btnLoader ? <div className='btnLoader'></div> : "Sign In"}</button>
                        </form>
                    </div>
                </div>
            </div>
        </div>
    )
}

export default INCSUBLoginPage;
